/**
 * OWNED BY — who may be named in `Filters.own`, and who may not.
 *
 * Marcus, 2026-08-27: *"users without steam links should be disabled with a
 * hover tooltip explaining why."* The disable is not decoration. An unlinked
 * member owns nothing anyone can see, so their key in `Filters.own` makes an
 * AND that no row can satisfy — every board goes blank and the panel gives no
 * reason. That is the trap this module exists to make unreachable, and it is
 * why the rule lives here rather than only in the two panels: the store's
 * `toggleOwn` runs it too, so no future caller can write a key that empties the
 * app by construction.
 *
 * THREE STATES, NOT TWO, and the third is the one that is easy to lose.
 * `crewLinks` is `null` until the roster read lands and a member can be missing
 * from a non-null map, so "we have not asked about this person" is reachable and
 * must NOT render as NO STEAM LINK — that fabrication is deviation P2-08, closed
 * by migration 3010 and re-openable by any surface that collapses `undefined`
 * into `false`. A pending member is disabled with a DIFFERENT tooltip, because
 * "they have not linked Steam" and "we do not know yet" are different sentences
 * and only the first is an instruction to that person.
 *
 * TWO SOURCES for the same fact, exactly as `crewVM` reads them and for the
 * reason recorded there: your own answer comes off `steamLink`, which link and
 * unlink keep current, because yours is the one row that can change mid-session;
 * everybody else's comes off `crewLinks`, fetched once per session.
 */

import type { User } from '../lib/types.ts';
import type { QdStore } from './types.ts';

/** What the panel needs to know about one member's Steam link. */
export type LinkState =
  /** Linked — selectable. */
  | 'linked'
  /** Asked, and they have not connected Steam. Disabled, with the reason. */
  | 'unlinked'
  /** Not asked yet, or the read failed. Disabled, and it says so honestly. */
  | 'pending';

type LinkSlice = Pick<QdStore, 'crewLinks' | 'steamLink'> & { me: QdStore['me'] };

export function linkStateOf(s: LinkSlice, u: Pick<User, 'k'>): LinkState {
  const me = s.me();
  if (me && u.k === me.k) {
    // `null` is PENDING, not unlinked, and the distinction is the same one the
    // crew arm makes below. `steamLink` is null until boot's parallel fetch
    // lands and again after any read that failed, so collapsing it into
    // `unlinked` tells the signed-in member they have not connected Steam —
    // about the one person in the roster who can see that it is false. It also
    // disables the name they are most likely to reach for first, which reads as
    // the whole section being broken rather than as one chip being early.
    return s.steamLink === null ? 'pending' : s.steamLink.linked ? 'linked' : 'unlinked';
  }
  const l = s.crewLinks?.[u.k];
  return l === undefined ? 'pending' : l ? 'linked' : 'unlinked';
}

/** The keys `Filters.own` is allowed to hold, in roster order. */
export function ownableKeys(s: LinkSlice & Pick<QdStore, 'users'>): string[] {
  return s.users.filter((u) => linkStateOf(s, u) === 'linked').map((u) => u.k);
}

/**
 * The tooltip on a member who cannot be picked, in the app's UPPERCASE mono
 * voice. `''` for a member who can — `components/Tooltip.tsx` shows nothing for
 * an empty `data-tip`, so the caller can set the attribute unconditionally.
 */
export function ownDisabledTip(st: LinkState, name: string): string {
  if (st === 'linked') return '';
  if (st === 'pending') return 'CHECKING WHETHER ' + name.toUpperCase() + ' HAS LINKED STEAM';
  return (
    name.toUpperCase() +
    ' HAS NOT LINKED STEAM, SO WE CANNOT SEE THEIR LIBRARY. THEY CAN LINK IT ON PLAYTESTS ▸ STEAM.'
  );
}

/**
 * The same reason in two words, printed ON the disabled chip.
 *
 * THE TOOLTIP IS NOT ENOUGH BY ITSELF and this is not belt-and-braces.
 * `components/Tooltip.tsx` deliberately shows nothing for a non-mouse
 * `pointerType`, so on the phone — where the filter sheet is the primary
 * surface — a hover-only explanation is no explanation. A greyed chip with no
 * stated reason reads as a bug. So the reason is on the chip for everyone and
 * the full sentence is the hover for those who can hover.
 */
export function ownDisabledBadge(st: LinkState): string {
  if (st === 'linked') return '';
  return st === 'pending' ? 'CHECKING' : 'NO LINK';
}
